import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Table,
  Tag,
  Space,
  Input,
  Select,
  message,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Switch,
  Progress,
  Tooltip,
  Typography,
  Upload,
  Divider,
  Descriptions,
  Timeline,
  Popconfirm,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import {
  PlusOutlined,
  SearchOutlined,
  DollarOutlined,
  RiseOutlined,
  CalendarOutlined,
  BarChartOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  InboxOutlined,
  FileTextOutlined,
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { BASE_API_URL } from "../../utils/consts";
import { usePermissions } from "../../utils/usePermissions";
import { isLocationActive } from "../../utils/helpers/locationVisibility";
import "./Investments.scss";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

interface Investment {
  _id: string;
  name: string;
  description?: string;
  categoryId?: {
    _id: string;
    name: string;
    color: string;
  };
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  status: string;
  purchaseDate: string;
  startDate?: string;
  expectedEndDate?: string;
  locationId?: {
    _id: string;
    name: string;
  };
  supplierId?: string;
  paymentMethod?: string;
  includesVAT?: boolean;
  vatAmount?: number;
  amountWithoutVAT?: number;
  hasDepreciation?: boolean;
  depreciationYears?: number;
  depreciatedAmount?: number;
  bookValue?: number;
  hasROI?: boolean;
  estimatedAnnualReturn?: number;
  roiPercentage?: number;
  documents?: Array<{
    name: string;
    url: string;
    uploadedAt: string;
  }>;
  paymentSchedule?: Array<{
    _id: string;
    dueDate: string;
    amount: number;
    isPaid: boolean;
    paidDate?: string;
    notes?: string;
  }>;
  completionPercentage?: number;
  depreciationProgress?: number;
  monthlyDepreciation?: number;
  annualDepreciation?: number;
  createdAt?: string;
}

interface Category {
  _id: string;
  name: string;
  color: string;
}

interface Location {
  _id: string;
  name: string;
}

interface Stats {
  totalInvestments: number;
  totalAmount: number;
  totalPaid: number;
  totalRemaining: number;
  averageCompletion: number;
}

const COLORS = [
  "#1890ff",
  "#52c41a",
  "#fa8c16",
  "#722ed1",
  "#eb2f96",
  "#13c2c2",
];

const InvestmentsComplete: React.FC = () => {
  const { t, i18n } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const clientId = userData?.clientId?._id || userData?.clientId;

  // Get currency from client settings - same logic as Payments page
  const getCurrencyIcon = () => {
    const currency = userData?.client?.currency || userData?.clientId?.currency;
    if (currency === "Dinar") return "RSD";
    if (currency === "Euro") return "€";
    if (currency === "Dollar") return "$";
    return "€"; // default
  };

  const currency = getCurrencyIcon();
  const userId = userData?.id;

  const { hasPermission } = usePermissions();
  const canCreate = hasPermission("investments:create");
  const canUpdate = hasPermission("investments:update");
  const canDelete = hasPermission("investments:delete");
  const canViewStats = hasPermission("investments:view-stats");

  // State
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalInvestments: 0,
    totalAmount: 0,
    totalPaid: 0,
    totalRemaining: 0,
    averageCompletion: 0,
  });
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [categoryData, setCategoryData] = useState<any[]>([]);
  const [paymentsList, setPaymentsList] = useState<any[]>([]);
  const [currentMonthStats, setCurrentMonthStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedInvestment, setSelectedInvestment] =
    useState<Investment | null>(null);
  const [selectedInvestmentForPayment, setSelectedInvestmentForPayment] =
    useState<Investment | null>(null);
  const [depreciationSchedule, setDepreciationSchedule] = useState<any[]>([]);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [selectedLocation, setSelectedLocation] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");

  // Month selector for current month stats
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() + 1
  );
  const [selectedMonthYear, setSelectedMonthYear] = useState<number>(
    new Date().getFullYear()
  );

  // Form watchers
  const watchIncludesVAT = Form.useWatch("includesVAT", form);
  const watchTotalAmount = Form.useWatch("totalAmount", form);
  const watchVATRate = Form.useWatch("vatRate", form);
  const watchHasDepreciation = Form.useWatch("hasDepreciation", form);
  const watchDepreciationYears = Form.useWatch("depreciationYears", form);
  const watchHasROI = Form.useWatch("hasROI", form);

  // Fetch functions
  const fetchInvestments = useCallback(async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      const params: any = { clientId };
      if (selectedLocation !== "all") params.locationId = selectedLocation;
      if (selectedCategory !== "all") params.categoryId = selectedCategory;
      if (selectedStatus !== "all") params.status = selectedStatus;

      const { data } = await axios.get(`${BASE_API_URL}/investments`, {
        params,
      });
      console.log("Fetched investments:", data);
      setInvestments(data || []);
    } catch (error) {
      console.error(error);
      message.error(t("Failed to load investments"));
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedLocation, selectedCategory, selectedStatus, t]);

  const fetchStats = useCallback(async () => {
    if (!clientId || !canViewStats) return;

    try {
      const { data } = await axios.get(`${BASE_API_URL}/investments/stats`, {
        params: { clientId, year: selectedYear },
      });
      setStats(data);
    } catch (error) {
      console.error(error);
    }
  }, [clientId, selectedYear, canViewStats]);

  const fetchMonthlyData = useCallback(async () => {
    if (!clientId || !canViewStats) return;

    try {
      const { data } = await axios.get(`${BASE_API_URL}/investments/by-month`, {
        params: { clientId, year: selectedYear },
      });
      setMonthlyData(data || []);
    } catch (error) {
      console.error(error);
    }
  }, [clientId, selectedYear, canViewStats]);

  const fetchCategoryData = useCallback(async () => {
    if (!clientId || !canViewStats) return;

    try {
      const { data } = await axios.get(
        `${BASE_API_URL}/investments/by-category`,
        {
          params: { clientId, year: selectedYear },
        }
      );
      setCategoryData(data || []);
    } catch (error) {
      console.error(error);
    }
  }, [clientId, selectedYear, canViewStats]);

  const fetchPaymentsList = useCallback(async () => {
    if (!clientId || !canViewStats) return;

    try {
      const { data } = await axios.get(
        `${BASE_API_URL}/investments/payments-list`,
        {
          params: { clientId, year: selectedYear },
        }
      );
      setPaymentsList(data || []);
    } catch (error) {
      console.error(error);
    }
  }, [clientId, selectedYear, canViewStats]);

  const fetchCurrentMonthStats = useCallback(async () => {
    if (!clientId || !canViewStats) return;

    try {
      console.log("Fetching stats for:", {
        clientId,
        year: selectedMonthYear,
        month: selectedMonth,
      });
      const { data } = await axios.get(
        `${BASE_API_URL}/investments/month-amount`,
        {
          params: { clientId, year: selectedMonthYear, month: selectedMonth },
        }
      );
      console.log("Received month stats:", data);
      setCurrentMonthStats(data);
    } catch (error) {
      console.error("Error fetching month stats:", error);
    }
  }, [clientId, canViewStats, selectedMonth, selectedMonthYear]);

  const fetchCategories = useCallback(async () => {
    if (!clientId) return;

    try {
      const { data } = await axios.get(
        `${BASE_API_URL}/investment-categories`,
        {
          params: { clientId },
        }
      );
      setCategories(data || []);
    } catch (error) {
      console.error(error);
    }
  }, [clientId]);

  // Month navigation handlers
  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedMonthYear(selectedMonthYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedMonthYear(selectedMonthYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const getMonthName = (month: number) => {
    const monthNames = {
      en: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      sr: [
        "Januar",
        "Februar",
        "Mart",
        "April",
        "Maj",
        "Jun",
        "Jul",
        "Avgust",
        "Septembar",
        "Oktobar",
        "Novembar",
        "Decembar",
      ],
      de: [
        "Januar",
        "Februar",
        "März",
        "April",
        "Mai",
        "Juni",
        "Juli",
        "August",
        "September",
        "Oktober",
        "November",
        "Dezember",
      ],
    };

    const currentLang = i18n.language || "en";
    const lang = currentLang.startsWith("sr")
      ? "sr"
      : currentLang.startsWith("de")
      ? "de"
      : "en";
    return monthNames[lang][month - 1] || monthNames["en"][month - 1];
  };

  const fetchLocations = useCallback(async () => {
    if (!clientId) return;

    try {
      const { data } = await axios.get(`${BASE_API_URL}/${clientId}/locations`);
      setLocations((data || []).filter(isLocationActive));
    } catch (error) {
      console.error(error);
    }
  }, [clientId]);

  const fetchDepreciationSchedule = async (investmentId: string) => {
    try {
      const { data } = await axios.get(
        `${BASE_API_URL}/investments/${investmentId}/depreciation-schedule`
      );
      setDepreciationSchedule(data.schedule || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchInvestments();
    fetchStats();
    fetchMonthlyData();
    fetchCategoryData();
    fetchPaymentsList();
    fetchCurrentMonthStats();
    fetchCategories();
    fetchLocations();
  }, [
    fetchInvestments,
    fetchStats,
    fetchMonthlyData,
    fetchCategoryData,
    fetchPaymentsList,
    fetchCurrentMonthStats,
    fetchCategories,
    fetchLocations,
  ]);

  // Fetch month stats when month/year changes
  useEffect(() => {
    if (selectedMonth && selectedMonthYear) {
      fetchCurrentMonthStats();
    }
  }, [selectedMonth, selectedMonthYear, fetchCurrentMonthStats]);

  // Filtered investments
  const filteredInvestments = useMemo(() => {
    const filtered = investments.filter((inv) => {
      const matchesSearch =
        inv.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.categoryId?.name?.toLowerCase().includes(searchTerm.toLowerCase());

      // Check if purchaseDate exists before filtering by year
      const matchesYear =
        !selectedYear ||
        !inv.purchaseDate ||
        new Date(inv.purchaseDate).getFullYear() === selectedYear;

      return matchesSearch && matchesYear;
    });
    console.log(
      "Filtered investments:",
      filtered.length,
      "from",
      investments.length
    );
    return filtered;
  }, [investments, searchTerm, selectedYear]);

  // Calculate VAT details
  const vatDetails = useMemo(() => {
    if (!watchIncludesVAT || !watchTotalAmount) {
      return { vatAmount: 0, amountWithoutVAT: watchTotalAmount || 0 };
    }

    const total = Number(watchTotalAmount);
    const vatRate = (Number(watchVATRate) || 20) / 100; // Dynamic VAT rate, default 20%
    const amountWithoutVAT = total / (1 + vatRate);
    const vatAmount = total - amountWithoutVAT;

    return {
      vatAmount: vatAmount.toFixed(2),
      amountWithoutVAT: amountWithoutVAT.toFixed(2),
    };
  }, [watchIncludesVAT, watchTotalAmount, watchVATRate]);

  // Calculate depreciation preview
  const depreciationPreview = useMemo(() => {
    if (!watchHasDepreciation || !watchDepreciationYears || !watchTotalAmount) {
      return { monthly: 0, annual: 0 };
    }

    const baseAmount = watchIncludesVAT
      ? Number(vatDetails.amountWithoutVAT)
      : Number(watchTotalAmount);
    const years = Number(watchDepreciationYears);
    const annual = baseAmount / years;
    const monthly = annual / 12;

    return {
      monthly: monthly.toFixed(2),
      annual: annual.toFixed(2),
    };
  }, [
    watchHasDepreciation,
    watchDepreciationYears,
    watchTotalAmount,
    watchIncludesVAT,
    vatDetails,
  ]);

  // CRUD Operations
  const handleCreateOrUpdate = async (values: any) => {
    try {
      const payload = {
        ...values,
        clientId,
        purchaseDate: values.purchaseDate?.format("YYYY-MM-DD"),
        startDate:
          values.startDate?.format("YYYY-MM-DD") ||
          values.purchaseDate?.format("YYYY-MM-DD"),
        expectedEndDate: values.expectedEndDate?.format("YYYY-MM-DD"),
        categoryId: values.categoryId || null,
        locationId: values.locationId || null,
        vatAmount: Number(vatDetails.vatAmount),
        amountWithoutVAT: Number(vatDetails.amountWithoutVAT),
        createdBy: userId,
        updatedBy: userId,
      };

      if (selectedInvestment) {
        await axios.put(
          `${BASE_API_URL}/investments/${selectedInvestment._id}`,
          payload
        );
        message.success(t("Investment updated successfully"));
      } else {
        await axios.post(`${BASE_API_URL}/investments`, payload);
        message.success(t("Investment created successfully"));
      }

      setModalVisible(false);
      form.resetFields();
      setFileList([]);
      fetchInvestments();
      fetchStats();
      fetchMonthlyData();
      fetchCategoryData();
      fetchPaymentsList();
    } catch (error: any) {
      console.error(error);
      message.error(error.response?.data?.message || t("Operation failed"));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${BASE_API_URL}/investments/${id}`);
      message.success(t("Investment deleted successfully"));
      fetchInvestments();
      fetchStats();
      fetchMonthlyData();
      fetchCategoryData();
      fetchPaymentsList();
    } catch (error) {
      console.error(error);
      message.error(t("Failed to delete investment"));
    }
  };

  const handleAddPayment = (investment: Investment) => {
    setSelectedInvestmentForPayment(investment);
    setEditingPaymentId(null);
    paymentForm.resetFields();
    paymentForm.setFieldsValue({
      amount: investment.remainingAmount,
      dueDate: dayjs(),
      notes: "",
    });
    setPaymentModalVisible(true);
  };

  const handleEditPayment = (payment: any) => {
    setEditingPaymentId(payment._id);
    paymentForm.setFieldsValue({
      amount: payment.amount,
      dueDate: dayjs(payment.dueDate),
      notes: payment.notes || "",
    });
  };

  const handleDeletePayment = async (paymentId: string) => {
    try {
      await axios.delete(
        `${BASE_API_URL}/investments/${selectedInvestmentForPayment?._id}/payment-schedule/${paymentId}`
      );

      message.success(t("Payment deleted successfully"));

      // Refresh investment data
      const { data } = await axios.get(
        `${BASE_API_URL}/investments/${selectedInvestmentForPayment?._id}`
      );
      setSelectedInvestmentForPayment(data);

      fetchInvestments();
      fetchStats();
      fetchMonthlyData();
      fetchCategoryData();
      fetchPaymentsList();
    } catch (error: any) {
      console.error(error);
      message.error(
        error.response?.data?.message || t("Failed to delete payment")
      );
    }
  };

  const handlePaymentSubmit = async () => {
    try {
      const values = await paymentForm.validateFields();

      if (editingPaymentId) {
        // UPDATE existing payment
        await axios.put(
          `${BASE_API_URL}/investments/${selectedInvestmentForPayment?._id}/payment-schedule/${editingPaymentId}`,
          {
            dueDate: values.dueDate,
            amount: values.amount,
            isPaid: true,
            paidDate: values.dueDate, // Use dueDate as paidDate
            notes: values.notes,
          }
        );
        message.success(t("Payment updated successfully"));
      } else {
        // CREATE new payment
        await axios.post(
          `${BASE_API_URL}/investments/${selectedInvestmentForPayment?._id}/payment-schedule`,
          {
            dueDate: values.dueDate,
            amount: values.amount,
            isPaid: true,
            paidDate: values.dueDate, // Use dueDate as paidDate
            notes: values.notes,
          }
        );
        message.success(t("Payment added successfully"));
      }

      setPaymentModalVisible(false);
      paymentForm.resetFields();
      setEditingPaymentId(null);
      fetchInvestments();
      fetchStats();
      fetchMonthlyData();
      fetchCategoryData();
      fetchPaymentsList();
    } catch (error: any) {
      console.error(error);
      message.error(
        error.response?.data?.message ||
          t(
            editingPaymentId
              ? "Failed to update payment"
              : "Failed to add payment"
          )
      );
    }
  };

  const handleEdit = (investment: Investment) => {
    setSelectedInvestment(investment);
    form.setFieldsValue({
      ...investment,
      categoryId: investment.categoryId?._id,
      locationId: investment.locationId?._id,
      purchaseDate: investment.purchaseDate
        ? dayjs(investment.purchaseDate)
        : null,
      startDate: investment.startDate ? dayjs(investment.startDate) : null,
      expectedEndDate: investment.expectedEndDate
        ? dayjs(investment.expectedEndDate)
        : null,
    });
    setModalVisible(true);
  };

  const handleViewDetails = async (investment: Investment) => {
    setSelectedInvestment(investment);
    if (investment.hasDepreciation) {
      await fetchDepreciationSchedule(investment._id);
    }
    setDetailModalVisible(true);
  };

  // KPI Cards
  const kpiCards = useMemo(
    () => [
      {
        title: t("Total Investments"),
        value: `${stats.totalAmount.toLocaleString()} ${currency}`,
        description: `${stats.totalInvestments} ${t("investments")}`,
        icon: <DollarOutlined />,
        type: "primary",
      },
      {
        title: t("Number of Investments"),
        value: stats.totalInvestments,
        description: t("Active investments"),
        icon: <BarChartOutlined />,
        type: "success",
      },
      {
        title: t("Total Paid"),
        value: `${stats.totalPaid.toLocaleString()} ${currency}`,
        description: `${stats.averageCompletion}% ${t("completed")}`,
        icon: <RiseOutlined />,
        type: "info",
      },
      {
        title: t("Remaining"),
        value: `${stats.totalRemaining.toLocaleString()} ${currency}`,
        description: t("Yet to be paid"),
        icon: <CalendarOutlined />,
        type: "warning",
      },
    ],
    [stats, currency, t]
  );

  // Table columns
  const columns = [
    {
      title: t("Name"),
      dataIndex: "name",
      key: "name",
      width: 200,
      fixed: "left" as const,
      render: (text: string, record: Investment) => (
        <div>
          <div style={{ fontWeight: 600 }}>{text}</div>
          {record.description && (
            <div style={{ fontSize: 12, color: "#666" }}>
              {record.description.substring(0, 50)}...
            </div>
          )}
        </div>
      ),
    },
    {
      title: t("Category"),
      dataIndex: "categoryId",
      key: "category",
      width: 150,
      render: (category: any) =>
        category ? (
          <span className="category-tag">
            <span
              className="category-color"
              style={{ backgroundColor: category.color }}
            />
            {category.name}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: t("Purchase Date"),
      dataIndex: "purchaseDate",
      key: "purchaseDate",
      width: 120,
      render: (date: string) => dayjs(date).format("DD.MM.YYYY"),
      sorter: (a: Investment, b: Investment) =>
        new Date(a.purchaseDate).getTime() - new Date(b.purchaseDate).getTime(),
    },
    {
      title: t("Amount"),
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 130,
      render: (amount: number) => (
        <span style={{ fontWeight: 600 }}>
          {amount.toLocaleString()} {currency}
        </span>
      ),
      sorter: (a: Investment, b: Investment) => a.totalAmount - b.totalAmount,
    },
    {
      title: t("Location"),
      dataIndex: "locationId",
      key: "location",
      width: 130,
      render: (location: any) => location?.name || "-",
    },
    {
      title: t("Status"),
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          Planning: "blue",
          "In Progress": "orange",
          Completed: "green",
          "On Hold": "gold",
          Cancelled: "red",
        };
        return <Tag color={colorMap[status] || "default"}>{t(status)}</Tag>;
      },
    },
    {
      title: t("Payment Progress"),
      key: "progress",
      width: 150,
      render: (_: any, record: Investment) => (
        <div className="depreciation-progress">
          <Progress
            percent={record.completionPercentage || 0}
            size="small"
            status={record.completionPercentage === 100 ? "success" : "active"}
          />
          <span>{record.completionPercentage || 0}%</span>
        </div>
      ),
    },
    {
      title: t("Depreciation"),
      key: "depreciation",
      width: 130,
      render: (_: any, record: Investment) => {
        if (!record.hasDepreciation) {
          return <Text type="secondary">-</Text>;
        }

        const progressBar = (
          <Progress
            percent={record.depreciationProgress || 0}
            size="small"
            strokeColor="#fa8c16"
          />
        );

        // Show tooltip only if bookValue is defined and greater than 0
        if (record.bookValue && record.bookValue > 0) {
          return (
            <Tooltip
              title={`${t(
                "Book Value"
              )}: ${record.bookValue.toLocaleString()} ${currency}`}
            >
              {progressBar}
            </Tooltip>
          );
        }

        return progressBar;
      },
    },
    {
      title: t("Documents"),
      dataIndex: "documents",
      key: "documents",
      width: 100,
      align: "center" as const,
      render: (documents: any[]) => (
        <span className="document-count">
          <FileTextOutlined />
          {documents?.length || 0}
        </span>
      ),
    },
    {
      title: t("Actions"),
      key: "actions",
      width: 140,
      fixed: "right" as const,
      render: (_: any, record: Investment) => (
        <Space size="small">
          <Tooltip title={t("View Details")}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {canUpdate && (
            <Tooltip title={t("Add Payment")}>
              <Button
                type="link"
                size="small"
                icon={<DollarOutlined />}
                onClick={() => handleAddPayment(record)}
                style={{ color: "#52c41a" }}
              />
            </Tooltip>
          )}
          {canUpdate && (
            <Tooltip title={t("Edit")}>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm
              title={t("Are you sure you want to delete this investment?")}
              onConfirm={() => handleDelete(record._id)}
              okText={t("Yes")}
              cancelText={t("No")}
            >
              <Tooltip title={t("Delete")}>
                <Button
                  type="primary"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="investments-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-top">
          <div>
            <h1>{t("Investments")}</h1>
            <Text className="header-subtitle">
              {t(
                "Track all business investments, equipment, renovations, and capital expenditures"
              )}
            </Text>
          </div>
          <div className="header-actions">
            {canCreate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                onClick={() => {
                  setSelectedInvestment(null);
                  form.resetFields();
                  setFileList([]);
                  setModalVisible(true);
                }}
              >
                {t("New Investment")}
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="filters-row">
          <Input
            placeholder={t("Search investments...")}
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
          <Select
            placeholder={t("Year")}
            value={selectedYear}
            onChange={setSelectedYear}
            style={{ minWidth: 120 }}
          >
            {Array.from(
              { length: 10 },
              (_, i) => new Date().getFullYear() - i
            ).map((year) => (
              <Select.Option key={year} value={year}>
                {year}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder={t("Location")}
            value={selectedLocation}
            onChange={setSelectedLocation}
            style={{ minWidth: 150 }}
          >
            <Select.Option value="all">{t("All Locations")}</Select.Option>
            {locations.map((loc) => (
              <Select.Option key={loc._id} value={loc._id}>
                {loc.name}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder={t("Category")}
            value={selectedCategory}
            onChange={setSelectedCategory}
            style={{ minWidth: 150 }}
          >
            <Select.Option value="all">{t("All Categories")}</Select.Option>
            {categories.map((cat) => (
              <Select.Option key={cat._id} value={cat._id}>
                {cat.name}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder={t("Status")}
            value={selectedStatus}
            onChange={setSelectedStatus}
            style={{ minWidth: 150 }}
          >
            <Select.Option value="all">{t("All Statuses")}</Select.Option>
            <Select.Option value="Planning">{t("Planning")}</Select.Option>
            <Select.Option value="In Progress">
              {t("In Progress")}
            </Select.Option>
            <Select.Option value="Completed">{t("Completed")}</Select.Option>
            <Select.Option value="On Hold">{t("On Hold")}</Select.Option>
            <Select.Option value="Cancelled">{t("Cancelled")}</Select.Option>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      {canViewStats && (
        <div className="kpi-cards">
          {kpiCards.map((card, index) => (
            <div key={index} className="kpi-card">
              <div className="kpi-header">
                <div className={`kpi-icon ${card.type}`}>{card.icon}</div>
              </div>
              <div className="kpi-title">{card.title}</div>
              <div className="kpi-value">{card.value}</div>
              <div className="kpi-description">{card.description}</div>
            </div>
          ))}
        </div>
      )}

      {/* Current Month Stats */}
      {canViewStats && currentMonthStats && (
        <div className="current-month-banner">
          <div className="banner-content">
            <div className="banner-icon">
              <CalendarOutlined />
            </div>
            <div className="banner-info">
              <div className="banner-header">
                <Button
                  type="text"
                  icon={<LeftOutlined />}
                  onClick={handlePreviousMonth}
                  className="month-nav-btn"
                />
                <h3 className="month-title">
                  {getMonthName(selectedMonth)} {selectedMonthYear}
                </h3>
                <Button
                  type="text"
                  icon={<RightOutlined />}
                  onClick={handleNextMonth}
                  className="month-nav-btn"
                />
              </div>
              <div className="banner-stats">
                <div className="banner-stat">
                  <span className="stat-label">{t("Invested")}:</span>
                  <span className="stat-value">
                    {currentMonthStats.totalInvested.toLocaleString()}{" "}
                    {currency}
                  </span>
                </div>
                {currentMonthStats.totalPaidInMonth > 0 && (
                  <div className="banner-stat">
                    <span className="stat-label">{t("Paid in month")}:</span>
                    <span className="stat-value paid">
                      {currentMonthStats.totalPaidInMonth.toLocaleString()}{" "}
                      {currency}
                    </span>
                  </div>
                )}
                <div className="banner-stat">
                  <span className="stat-label">{t("Investments")}:</span>
                  <span className="stat-value">
                    {currentMonthStats.investmentCount}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      {canViewStats && (
        <div className="charts-section">
          {/* Monthly Chart */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>
                {t("Investments by Month")} - {selectedYear}
              </h3>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="monthName" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar
                    dataKey="totalAmount"
                    fill="#1890ff"
                    name={t("Amount")}
                  />
                  <Bar dataKey="count" fill="#52c41a" name={t("Count")} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Category Pie Chart */}
          <div className="chart-card">
            <div className="chart-header">
              <h3>{t("By Category")}</h3>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    dataKey="totalAmount"
                    nameKey="categoryName"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={(entry: any) =>
                      `${
                        entry.categoryName
                      }: ${entry.totalAmount.toLocaleString()}`
                    }
                  >
                    {categoryData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.categoryColor || COLORS[index % COLORS.length]
                        }
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Payments List Table */}
      {canViewStats && (
        <div className="investments-table-card" style={{ marginBottom: 32 }}>
          <div className="table-header">
            <h3>
              {t("Monthly Payment Schedule")} - {selectedYear}
            </h3>
            <Text type="secondary">
              {paymentsList.length} {t("payments")}
            </Text>
          </div>
          <Table
            dataSource={paymentsList}
            loading={loading}
            rowKey="_id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `${t("Total")} ${total} ${t("payments")}`,
            }}
            columns={[
              {
                title: t("Investment"),
                dataIndex: "investmentName",
                key: "investmentName",
                width: 200,
                render: (text: string, record: any) => (
                  <div>
                    <div style={{ fontWeight: 600 }}>{text}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: record.categoryColor,
                          marginRight: 4,
                        }}
                      />
                      {record.categoryName}
                    </div>
                  </div>
                ),
              },
              {
                title: t("Location"),
                dataIndex: "locationName",
                key: "locationName",
                width: 130,
              },
              {
                title: t("Amount"),
                dataIndex: "amount",
                key: "amount",
                width: 130,
                render: (amount: number) => (
                  <strong>
                    {amount.toLocaleString()} {currency}
                  </strong>
                ),
                sorter: (a: any, b: any) => a.amount - b.amount,
              },
              {
                title: t("Due Date"),
                dataIndex: "dueDate",
                key: "dueDate",
                width: 120,
                render: (date: string) => dayjs(date).format("DD.MM.YYYY"),
              },
              {
                title: t("Paid Date"),
                dataIndex: "paidDate",
                key: "paidDate",
                width: 120,
                render: (date: string) =>
                  date ? dayjs(date).format("DD.MM.YYYY") : "-",
              },
              {
                title: t("Status"),
                dataIndex: "isPaid",
                key: "isPaid",
                width: 100,
                render: (isPaid: boolean) => (
                  <Tag color={isPaid ? "green" : "orange"}>
                    {isPaid ? t("Paid") : t("Unpaid")}
                  </Tag>
                ),
                filters: [
                  { text: t("Paid"), value: true },
                  { text: t("Unpaid"), value: false },
                ],
                onFilter: (value: any, record: any) => record.isPaid === value,
              },
              {
                title: t("Notes"),
                dataIndex: "notes",
                key: "notes",
                ellipsis: true,
                render: (notes: string) => notes || "-",
              },
            ]}
          />
        </div>
      )}

      {/* Investments Table */}
      <div className="investments-table-card">
        <div className="table-header">
          <h3>{t("All Investments")}</h3>
          <Text type="secondary">
            {filteredInvestments.length} {t("investments")}
          </Text>
        </div>
        <Table
          columns={columns}
          dataSource={filteredInvestments}
          loading={loading}
          rowKey="_id"
          scroll={{ x: 1400 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `${t("Total")} ${total} ${t("investments")}`,
          }}
        />
      </div>

      {/* Create/Edit Modal - Continued in next part */}
      <Modal
        title={selectedInvestment ? t("Edit Investment") : t("New Investment")}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setFileList([]);
        }}
        footer={null}
        width={900}
        className="investment-modal standardHeader standardFooter"
      >
        <Form form={form} layout="vertical" onFinish={handleCreateOrUpdate}>
          {/* Section 1: Basic Info */}
          <div className="form-section">
            <div className="section-title">{t("Basic Information")}</div>
            <div className="inline-fields">
              <Form.Item
                label={t("Investment Name")}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("Please enter investment name"),
                  },
                ]}
              >
                <Input placeholder={t("e.g. New Dental Chair")} />
              </Form.Item>

              <Form.Item
                label={t("Category")}
                name="categoryId"
                rules={[
                  { required: true, message: t("Please select category") },
                ]}
              >
                <Select placeholder={t("Select category")}>
                  {categories.map((cat) => (
                    <Select.Option key={cat._id} value={cat._id}>
                      <span
                        style={{
                          display: "inline-block",
                          width: 12,
                          height: 12,
                          borderRadius: 3,
                          backgroundColor: cat.color,
                          marginRight: 8,
                        }}
                      />
                      {cat.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <Form.Item label={t("Description")} name="description">
              <TextArea rows={3} placeholder={t("Investment description...")} />
            </Form.Item>

            <div className="inline-fields">
              <Form.Item label={t("Location")} name="locationId">
                <Select placeholder={t("Select location")} allowClear>
                  {locations.map((loc) => (
                    <Select.Option key={loc._id} value={loc._id}>
                      {loc.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label={t("Supplier")} name="supplierId">
                <Input placeholder={t("Supplier name or ID")} />
              </Form.Item>
            </div>

            <div className="inline-fields">
              <Form.Item
                label={t("Purchase Date")}
                name="purchaseDate"
                rules={[
                  { required: true, message: t("Please select purchase date") },
                ]}
              >
                <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
              </Form.Item>

              <Form.Item
                label={t("Status")}
                name="status"
                initialValue="Planning"
              >
                <Select>
                  <Select.Option value="Planning">
                    {t("Planning")}
                  </Select.Option>
                  <Select.Option value="In Progress">
                    {t("In Progress")}
                  </Select.Option>
                  <Select.Option value="Completed">
                    {t("Completed")}
                  </Select.Option>
                  <Select.Option value="On Hold">{t("On Hold")}</Select.Option>
                  <Select.Option value="Cancelled">
                    {t("Cancelled")}
                  </Select.Option>
                </Select>
              </Form.Item>
            </div>
          </div>

          <Divider />

          {/* Section 2: Financial Details */}
          <div className="form-section">
            <div className="section-title">{t("Financial Details")}</div>
            <div className="inline-fields">
              <Form.Item
                label={t("Total Amount")}
                name="totalAmount"
                rules={[
                  { required: true, message: t("Please enter total amount") },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="0.00"
                  addonAfter={currency}
                />
              </Form.Item>

              <Form.Item
                label={t("Payment Method")}
                name="paymentMethod"
                initialValue="bank_transfer"
              >
                <Select>
                  <Select.Option value="cash">{t("Cash")}</Select.Option>
                  <Select.Option value="card">{t("Card")}</Select.Option>
                  <Select.Option value="bank_transfer">
                    {t("Bank Transfer")}
                  </Select.Option>
                  <Select.Option value="check">{t("Check")}</Select.Option>
                  <Select.Option value="other">{t("Other")}</Select.Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              label={t("Includes VAT")}
              name="includesVAT"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>

            {watchIncludesVAT && (
              <Form.Item
                label={t("VAT Rate (%)")}
                name="vatRate"
                initialValue={20}
                rules={[
                  { required: true, message: t("Please enter VAT rate") },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  placeholder="20"
                  addonAfter="%"
                />
              </Form.Item>
            )}

            {watchIncludesVAT && watchTotalAmount > 0 && (
              <div className="vat-calculation">
                <div className="calc-row">
                  <span>{t("Amount without VAT")}:</span>
                  <strong>
                    {vatDetails.amountWithoutVAT} {currency}
                  </strong>
                </div>
                <div className="calc-row">
                  <span>
                    {t("VAT Amount")} ({watchVATRate || 20}%):
                  </span>
                  <strong>
                    {vatDetails.vatAmount} {currency}
                  </strong>
                </div>
                <div className="calc-row total">
                  <span>{t("Total with VAT")}:</span>
                  <strong>
                    {watchTotalAmount} {currency}
                  </strong>
                </div>
              </div>
            )}
          </div>

          <Divider />

          {/* Section 3: Depreciation */}
          <div className="form-section">
            <div className="section-title">
              {t("Depreciation (Amortization)")}
            </div>
            <Form.Item
              label={t("Enable Depreciation")}
              name="hasDepreciation"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch />
            </Form.Item>

            {watchHasDepreciation && (
              <>
                <div className="inline-fields">
                  <Form.Item
                    label={t("Depreciation Years")}
                    name="depreciationYears"
                    initialValue={5}
                    rules={[
                      {
                        required: true,
                        message: t("Please enter depreciation years"),
                      },
                    ]}
                  >
                    <InputNumber style={{ width: "100%" }} min={1} max={30} />
                  </Form.Item>

                  <Form.Item
                    label={t("Depreciation Method")}
                    name="depreciationMethod"
                    initialValue="straight_line"
                  >
                    <Select>
                      <Select.Option value="straight_line">
                        {t("Straight Line")}
                      </Select.Option>
                      <Select.Option value="declining_balance">
                        {t("Declining Balance")}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </div>

                {watchDepreciationYears && watchTotalAmount > 0 && (
                  <div className="depreciation-preview">
                    <div className="preview-row">
                      <span>{t("Monthly Depreciation")}:</span>
                      <span>
                        {depreciationPreview.monthly} {currency}
                      </span>
                    </div>
                    <div className="preview-row">
                      <span>{t("Annual Depreciation")}:</span>
                      <span>
                        {depreciationPreview.annual} {currency}
                      </span>
                    </div>
                    <div className="preview-row">
                      <span>{t("Full Depreciation Period")}:</span>
                      <span>
                        {watchDepreciationYears} {t("years")}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <Divider />

          {/* Section 4: ROI */}
          <div className="form-section">
            <div className="section-title">
              {t("Return on Investment (ROI)")}
            </div>
            <Form.Item
              label={t("Track ROI")}
              name="hasROI"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch />
            </Form.Item>

            {watchHasROI && (
              <div className="inline-fields">
                <Form.Item
                  label={t("Estimated Annual Return")}
                  name="estimatedAnnualReturn"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    placeholder="0.00"
                    addonAfter={currency}
                  />
                </Form.Item>

                <Form.Item
                  label={t("Actual Annual Return")}
                  name="actualAnnualReturn"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    placeholder="0.00"
                    addonAfter={currency}
                  />
                </Form.Item>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" size="large">
                {selectedInvestment ? t("Update") : t("Create")}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setFileList([]);
                }}
                size="large"
              >
                {t("Cancel")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={t("Investment Details")}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            {t("Close")}
          </Button>,
        ]}
        width={1000}
        className="investment-detail-modal standardHeader standardFooter"
      >
        {selectedInvestment && (
          <div className="detail-sections">
            {/* Basic Info Section */}
            <div className="detail-section">
              <h3>{t("Basic Information")}</h3>
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label={t("Name")}>
                  {selectedInvestment.name}
                </Descriptions.Item>
                <Descriptions.Item label={t("Category")}>
                  {selectedInvestment.categoryId ? (
                    <Tag color={selectedInvestment.categoryId.color}>
                      {selectedInvestment.categoryId.name}
                    </Tag>
                  ) : (
                    "-"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t("Purchase Date")}>
                  {dayjs(selectedInvestment.purchaseDate).format("DD.MM.YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label={t("Status")}>
                  <Tag>{t(selectedInvestment.status)}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t("Location")}>
                  {selectedInvestment.locationId?.name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("Supplier")}>
                  {selectedInvestment.supplierId || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("Description")} span={2}>
                  {selectedInvestment.description || "-"}
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* Financial Section */}
            <div className="detail-section">
              <h3>{t("Financial Details")}</h3>
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label={t("Total Amount")}>
                  <strong>
                    {selectedInvestment.totalAmount.toLocaleString()} {currency}
                  </strong>
                </Descriptions.Item>
                <Descriptions.Item label={t("Payment Method")}>
                  {t(selectedInvestment.paymentMethod || "bank_transfer")}
                </Descriptions.Item>
                <Descriptions.Item label={t("Paid Amount")}>
                  {selectedInvestment.paidAmount.toLocaleString()} {currency}
                </Descriptions.Item>
                <Descriptions.Item label={t("Remaining Amount")}>
                  {selectedInvestment.remainingAmount.toLocaleString()}{" "}
                  {currency}
                </Descriptions.Item>
                {selectedInvestment.includesVAT && (
                  <>
                    <Descriptions.Item label={t("Amount without VAT")}>
                      {selectedInvestment.amountWithoutVAT?.toLocaleString()}{" "}
                      {currency}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("VAT Amount")}>
                      {selectedInvestment.vatAmount?.toLocaleString()}{" "}
                      {currency}
                    </Descriptions.Item>
                  </>
                )}
                <Descriptions.Item label={t("Payment Progress")}>
                  <Progress
                    percent={selectedInvestment.completionPercentage || 0}
                    status={
                      selectedInvestment.completionPercentage === 100
                        ? "success"
                        : "active"
                    }
                  />
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* Depreciation Section */}
            {selectedInvestment.hasDepreciation && (
              <div className="detail-section">
                <h3>{t("Depreciation Schedule")}</h3>
                <Descriptions
                  column={2}
                  bordered
                  size="small"
                  style={{ marginBottom: 16 }}
                >
                  <Descriptions.Item label={t("Depreciation Years")}>
                    {selectedInvestment.depreciationYears}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Monthly Depreciation")}>
                    {selectedInvestment.monthlyDepreciation?.toLocaleString()}{" "}
                    {currency}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Annual Depreciation")}>
                    {selectedInvestment.annualDepreciation?.toLocaleString()}{" "}
                    {currency}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Current Book Value")}>
                    <strong>
                      {selectedInvestment.bookValue?.toLocaleString()}{" "}
                      {currency}
                    </strong>
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t("Depreciation Progress")}
                    span={2}
                  >
                    <Progress
                      percent={selectedInvestment.depreciationProgress || 0}
                      strokeColor="#fa8c16"
                    />
                  </Descriptions.Item>
                </Descriptions>

                {depreciationSchedule.length > 0 && (
                  <Table
                    dataSource={depreciationSchedule}
                    columns={[
                      {
                        title: t("Year"),
                        dataIndex: "year",
                        key: "year",
                        width: 100,
                        className: "year-column",
                      },
                      {
                        title: t("Annual Depreciation"),
                        dataIndex: "annualDepreciation",
                        key: "annualDepreciation",
                        render: (val: string) => `${val} ${currency}`,
                      },
                      {
                        title: t("Cumulative Depreciation"),
                        dataIndex: "cumulativeDepreciation",
                        key: "cumulativeDepreciation",
                        render: (val: string) => `${val} ${currency}`,
                      },
                      {
                        title: t("Book Value"),
                        dataIndex: "bookValue",
                        key: "bookValue",
                        render: (val: string) => (
                          <strong>
                            {val} {currency}
                          </strong>
                        ),
                      },
                      {
                        title: t("Progress"),
                        dataIndex: "percentage",
                        key: "percentage",
                        width: 150,
                        className: "percentage-cell",
                        render: (val: string) => (
                          <Progress percent={Number(val)} size="small" />
                        ),
                      },
                    ]}
                    pagination={false}
                    size="small"
                    className="depreciation-table"
                    rowKey="year"
                  />
                )}
              </div>
            )}

            {/* ROI Section */}
            {selectedInvestment.hasROI && (
              <div className="detail-section">
                <h3>{t("Return on Investment")}</h3>
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label={t("Estimated Annual Return")}>
                    {selectedInvestment.estimatedAnnualReturn?.toLocaleString()}{" "}
                    {currency}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("ROI Percentage")}>
                    <Tag color="green">{selectedInvestment.roiPercentage}%</Tag>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}

            {/* Documents Section */}
            {selectedInvestment.documents &&
              selectedInvestment.documents.length > 0 && (
                <div className="detail-section">
                  <h3>{t("Documents")}</h3>
                  <div className="documents-grid">
                    {selectedInvestment.documents.map((doc, index) => (
                      <div key={index} className="document-card">
                        <div className="document-icon">
                          <FileTextOutlined />
                        </div>
                        <div className="document-name">{doc.name}</div>
                        <Button
                          type="link"
                          size="small"
                          icon={<DownloadOutlined />}
                          href={doc.url}
                          target="_blank"
                        >
                          {t("Download")}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
        )}
      </Modal>

      {/* Payment Modal */}
      <Modal
        title={editingPaymentId ? t("Edit Payment") : t("Add Payment")}
        open={paymentModalVisible}
        onOk={handlePaymentSubmit}
        onCancel={() => {
          setPaymentModalVisible(false);
          paymentForm.resetFields();
          setEditingPaymentId(null);
        }}
        okText={editingPaymentId ? t("Update Payment") : t("Add Payment")}
        className="standardHeader standardFooter"
        cancelText={t("Cancel")}
        width={700}
      >
        {/* Existing Payments List */}
        {selectedInvestmentForPayment?.paymentSchedule &&
          selectedInvestmentForPayment.paymentSchedule.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ marginBottom: 12 }}>{t("Payment History")}</h4>
              <Table
                dataSource={selectedInvestmentForPayment.paymentSchedule}
                pagination={false}
                size="small"
                rowKey="_id"
                columns={[
                  {
                    title: t("Date"),
                    dataIndex: "dueDate",
                    key: "dueDate",
                    width: 110,
                    render: (date: string) => dayjs(date).format("DD.MM.YYYY"),
                  },
                  {
                    title: t("Amount"),
                    dataIndex: "amount",
                    key: "amount",
                    width: 120,
                    render: (amount: number) => (
                      <strong>
                        {amount.toLocaleString()} {currency}
                      </strong>
                    ),
                  },
                  {
                    title: t("Status"),
                    dataIndex: "isPaid",
                    key: "isPaid",
                    width: 90,
                    render: (isPaid: boolean) => (
                      <Tag color={isPaid ? "green" : "orange"}>
                        {isPaid ? t("Paid") : t("Unpaid")}
                      </Tag>
                    ),
                  },
                  {
                    title: t("Notes"),
                    dataIndex: "notes",
                    key: "notes",
                    ellipsis: true,
                    render: (notes: string) => notes || "-",
                  },
                  {
                    title: t("Actions"),
                    key: "actions",
                    width: 100,
                    render: (_: any, record: any) => (
                      <Space size="small">
                        <Tooltip title={t("Edit")}>
                          <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditPayment(record)}
                          />
                        </Tooltip>
                        <Popconfirm
                          title={t(
                            "Are you sure you want to delete this payment?"
                          )}
                          onConfirm={() => handleDeletePayment(record._id)}
                          okText={t("Yes")}
                          cancelText={t("No")}
                        >
                          <Tooltip title={t("Delete")}>
                            <Button
                              type="primary"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                            />
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
              />
              <Divider />
            </div>
          )}

        {/* Payment Form */}
        <Form form={paymentForm} layout="vertical">
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: "#f5f7fa",
              borderRadius: 8,
            }}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text strong>{t("Investment")}:</Text>
                <Text>{selectedInvestmentForPayment?.name}</Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text strong>{t("Total Amount")}:</Text>
                <Text>
                  {selectedInvestmentForPayment?.totalAmount?.toLocaleString()}{" "}
                  {currency}
                </Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text strong>{t("Already Paid")}:</Text>
                <Text style={{ color: "#52c41a" }}>
                  {selectedInvestmentForPayment?.paidAmount?.toLocaleString()}{" "}
                  {currency}
                </Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text strong>{t("Remaining")}:</Text>
                <Text style={{ color: "#ff4d4f" }}>
                  {selectedInvestmentForPayment?.remainingAmount?.toLocaleString()}{" "}
                  {currency}
                </Text>
              </div>
            </Space>
          </div>

          <Form.Item
            name="amount"
            label={t("Payment Amount")}
            rules={[
              { required: true, message: t("Please enter payment amount") },
              {
                validator: (_, value) => {
                  if (
                    !editingPaymentId &&
                    value &&
                    value > (selectedInvestmentForPayment?.remainingAmount || 0)
                  ) {
                    return Promise.reject(
                      new Error(
                        t("Payment amount cannot exceed remaining amount")
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              placeholder={t("Enter amount")}
              min={0}
              addonAfter={currency}
            />
          </Form.Item>

          <Form.Item
            name="dueDate"
            label={t("Payment Date")}
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>

          <Form.Item name="notes" label={t("Notes")}>
            <Input.TextArea rows={3} placeholder={t("Add notes (optional)")} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InvestmentsComplete;
